import React, { useCallback, useContext, useMemo } from 'react';

export type ToastData = {
    id: string;
    groupId?: string;
    children: React.ReactNode;
    className?: string;
    props?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
    /**
     * When true, `ToastRenderer` skips wrapping this toast in the grouped `CSSTransitionGroup` (and
     * its `fade-and-slide-*` classes), because the toast drives its own enter/exit motion.
     */
    ownsMotion?: boolean;
};

type ToastRendererContextArgs = Map<string, ToastData>;

const ToastRendererContext = React.createContext<ToastRendererContextArgs>(new Map());

export const useToastRendererContext = () => useContext(ToastRendererContext);

type ToastContextArgs = {
    addToast: (toast: ToastData) => void;
    removeToast: (toastId: string) => void;
};

const ToastContext = React.createContext<ToastContextArgs>({
    addToast: () => {},
    removeToast: () => {},
});

export const useToastContext = () => useContext(ToastContext);

export const ToastProvider: React.FC = ({ children }) => {
    const [toasts, setToasts] = React.useState(new Map<string, ToastData>());

    const addToast = useCallback((toastData: ToastData) => {
        setToasts((prevToasts) => new Map(prevToasts).set(toastData.id, toastData));
    }, []);

    const removeToast = useCallback((toastId: string) => {
        setToasts((prevToasts) => {
            const newToasts = new Map(prevToasts);
            newToasts.delete(toastId);
            return newToasts;
        });
    }, []);

    const toastContextValue = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast]);

    return (
        <ToastRendererContext.Provider value={toasts}>
            <ToastContext.Provider value={toastContextValue}>{children}</ToastContext.Provider>
        </ToastRendererContext.Provider>
    );
};
